/**
 * Push Notification Settings Component
 * Allows users to manage their push notification preferences
 */

import React from 'react';
import { Bell, BellOff, Check, X, AlertCircle, Loader } from 'lucide-react';
import usePushNotifications from '../hooks/usePushNotifications';

const PushNotificationSettings = () => {
  const {
    permissionStatus,
    isInitialized,
    isLoading,
    error,
    requestPermission,
    sendTestNotification
  } = usePushNotifications();

  /**
   * Get status display info
   */
  const getStatusInfo = () => {
    switch (permissionStatus) {
      case 'granted':
        return {
          icon: <Check className="w-5 h-5 text-green-500" />,
          text: 'Enabled',
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200'
        };
      case 'denied':
        return {
          icon: <X className="w-5 h-5 text-red-500" />,
          text: 'Blocked',
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200'
        };
      case 'not-supported':
        return {
          icon: <AlertCircle className="w-5 h-5 text-gray-500" />,
          text: 'Not Supported',
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200'
        };
      default:
        return {
          icon: <BellOff className="w-5 h-5 text-yellow-500" />,
          text: 'Not Set',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200'
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center space-x-3 mb-4">
        <Bell className="w-6 h-6 text-green-500" />
        <h3 className="text-lg font-semibold text-gray-900">Push Notifications</h3>
      </div>

      <div className="space-y-4">
        {/* Status Display */}
        <div className={`flex items-center justify-between p-4 rounded-lg border ${statusInfo.bgColor} ${statusInfo.borderColor}`}>
          <div className="flex items-center space-x-3">
            {statusInfo.icon}
            <div>
              <p className={`font-medium ${statusInfo.color}`}>
                Status: {statusInfo.text}
              </p>
              {isInitialized && (
                <p className="text-sm text-gray-600">
                  You'll receive notifications for messages, connection requests, and updates
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="flex items-center space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {permissionStatus === 'default' && (
            <button
              onClick={requestPermission}
              disabled={isLoading}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Bell className="w-5 h-5" />
              )}
              <span>Enable Push Notifications</span>
            </button>
          )}

          {permissionStatus === 'denied' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Push notifications are blocked. To enable them:
              </p>
              <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                <li>Click the lock icon in your browser's address bar</li>
                <li>Select "Allow" for notifications</li>
                <li>Refresh this page</li>
              </ol>
            </div>
          )}

          {permissionStatus === 'granted' && isInitialized && (
            <button
              onClick={sendTestNotification}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Bell className="w-5 h-5" />
              <span>Send Test Notification</span>
            </button>
          )}

          {permissionStatus === 'not-supported' && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-600">
                Push notifications are not supported on this device or browser. 
                Try using a modern browser like Chrome, Firefox, or Safari.
              </p>
            </div>
          )}
        </div>

        {/* Notification Types */}
        {isInitialized && (
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">You'll receive notifications for:</h4>
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">New connection requests</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">New messages</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Job application updates</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Resource review results</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PushNotificationSettings;